import { Home, Package, ScanLine, History, User } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { useTranslation } from "react-i18next"
import { T } from "./theme"
import { Touchable } from "./primitives"

export type TabKey = "home" | "inventory" | "scan" | "activity" | "profile"

interface Tab {
  key: TabKey
  labelKey: string | null
  icon: LucideIcon
  center?: boolean
}

const ADMIN_TABS: Tab[] = [
  { key: "home",      labelKey: "mobile.tabs.home",      icon: Home },
  { key: "inventory", labelKey: "mobile.tabs.inventory", icon: Package },
  { key: "scan",      labelKey: null,                    icon: ScanLine, center: true },
  { key: "activity",  labelKey: "mobile.tabs.activity",  icon: History },
  { key: "profile",   labelKey: "mobile.tabs.profile",   icon: User },
]

const REGULAR_TABS: Tab[] = [
  { key: "home",    labelKey: "mobile.tabs.home",    icon: Home },
  { key: "scan",    labelKey: null,                  icon: ScanLine, center: true },
  { key: "profile", labelKey: "mobile.tabs.profile", icon: User },
]

interface TabBarProps {
  active: TabKey
  onChange: (tab: TabKey) => void
  role: "admin" | "regular"
}

export function TabBar({ active, onChange, role }: TabBarProps) {
  const { t } = useTranslation()
  const tabs = role === "admin" ? ADMIN_TABS : REGULAR_TABS
  return (
    <nav
      style={{
        position: "absolute",
        left: 0, right: 0, bottom: 0,
        zIndex: 10,
        height: T.tabBarHeight + 16,
        paddingBottom: 16,
        background: T.surface,
        borderTop: `1px solid ${T.borderSoft}`,
        display: "flex",
        alignItems: "stretch",
      }}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.key
        const Icon = tab.icon
        if (tab.center) {
          return (
            <div
              key={tab.key}
              style={{ flex: 1, position: "relative", display: "grid", placeItems: "center" }}
            >
              <Touchable
                onClick={() => onChange(tab.key)}
                style={{
                  position: "absolute",
                  top: -22,
                  width: 64, height: 64,
                  borderRadius: 999,
                  background: T.accent,
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  boxShadow: `0 4px 16px -4px rgba(232, 84, 28, 0.6), 0 0 0 6px ${T.surface}`,
                  border: `2px solid ${T.ink}`,
                }}
              >
                <Icon size={28} color="#fff" strokeWidth={2} />
              </Touchable>
            </div>
          )
        }
        return (
          <Touchable
            key={tab.key}
            onClick={() => onChange(tab.key)}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              color: isActive ? T.accent : T.inkSoft,
            }}
          >
            <Icon
              size={22}
              color={isActive ? T.accent : T.inkSoft}
              strokeWidth={isActive ? 2 : 1.75}
            />
            <span
              style={{
                fontFamily: T.fontBody,
                fontSize: 10.5,
                fontWeight: isActive ? 600 : 500,
                letterSpacing: 0.1,
              }}
            >
              {tab.labelKey ? t(tab.labelKey) : ""}
            </span>
          </Touchable>
        )
      })}
    </nav>
  )
}
